import { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, Clock, CheckCircle, XCircle, Truck, MessageCircle, Edit2, Copy, ExternalLink, Hash, Package } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = [
    { value: 'nuevo', label: 'Nuevo', icon: Clock, color: 'bg-amber-400/15 text-amber-400' },
    { value: 'confirmado', label: 'Confirmado', icon: CheckCircle, color: 'bg-blue-400/15 text-blue-400' },
    { value: 'enviado', label: 'Enviado', icon: Truck, color: 'bg-purple-400/15 text-purple-400' },
    { value: 'completado', label: 'Entregado', icon: CheckCircle, color: 'bg-emerald-400/15 text-emerald-400' },
    { value: 'cancelado', label: 'Cancelado', icon: XCircle, color: 'bg-red-400/15 text-red-400' },
];

const PAQUETERIAS_PRESET = [
    { v: 'DHL', url: 'https://www.dhl.com/mx-es/home/tracking.html?tracking-id=' },
    { v: 'Estafeta', url: 'https://www.estafeta.com/Herramientas/Rastreo?wayBillType=2&wayBill=' },
    { v: 'Paquetexpress', url: 'https://rastreo.paquetexpress.com.mx/?guia=' },
    { v: 'FedEx', url: 'https://www.fedex.com/fedextrack/?trknbr=' },
    { v: 'Redpack', url: 'https://www.redpack.com.mx/es/rastreo/?guias=' },
    { v: 'iVoy', url: '' },
    { v: 'Otro', url: '' }
];

export const Pedidos = () => {
    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtroEstado, setFiltroEstado] = useState('todos');
    const [editModal, setEditModal] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => { fetchPedidos(); }, []);

    const fetchPedidos = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/pedidos`);
            setPedidos(res.data);
        } catch (e) { toast.error('Error al cargar pedidos'); }
        finally { setLoading(false); }
    };

    const updateEstado = async (pedidoId, estado) => {
        try {
            await axios.put(`${API_URL}/api/pedidos/${pedidoId}/estado`, { estado });
            toast.success('Estado actualizado');
            fetchPedidos();
        } catch (e) { toast.error('Error al actualizar'); }
    };

    const openEdit = (pedido) => {
        setEditModal({
            ...pedido,
            paqueteria: pedido.paqueteria || '',
            numero_rastreo: pedido.numero_rastreo || '',
            tracking_url: pedido.tracking_url || '',
            estado: pedido.estado || 'nuevo'
        });
    };

    const onPaqueteriaChange = (val) => {
        const preset = PAQUETERIAS_PRESET.find(p => p.v === val);
        // Si la paqueteria tiene preset y hay numero de guia, auto-armar URL
        const autoUrl = preset && preset.url && editModal.numero_rastreo
            ? preset.url + editModal.numero_rastreo
            : editModal.tracking_url;
        setEditModal({ ...editModal, paqueteria: val, tracking_url: autoUrl || '' });
    };

    const onNumeroRastreoChange = (val) => {
        const preset = PAQUETERIAS_PRESET.find(p => p.v === editModal.paqueteria);
        const autoUrl = preset && preset.url && val ? preset.url + val : editModal.tracking_url;
        setEditModal({ ...editModal, numero_rastreo: val, tracking_url: autoUrl || '' });
    };

    const saveEdit = async () => {
        if (!editModal) return;
        setSavingEdit(true);
        try {
            await axios.put(`${API_URL}/api/pedidos/${editModal.id}`, {
                estado: editModal.estado,
                paqueteria: editModal.paqueteria,
                numero_rastreo: editModal.numero_rastreo,
                tracking_url: editModal.tracking_url,
                notas: editModal.notas || ''
            });
            toast.success('Pedido actualizado');
            setEditModal(null);
            fetchPedidos();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar');
        } finally {
            setSavingEdit(false);
        }
    };

    const copyTrackingLink = (pedido) => {
        const url = `${window.location.origin}/pedido/${pedido.codigo_seguimiento}`;
        navigator.clipboard.writeText(url).then(() => {
            toast.success('Link copiado al portapapeles');
        }).catch(() => {
            // fallback
            prompt('Copia el link manualmente:', url);
        });
    };

    const sendTrackingByWA = (pedido) => {
        const url = `${window.location.origin}/pedido/${pedido.codigo_seguimiento}`;
        const msg = `Hola ${pedido.cliente_nombre || ''}! Tu pedido en Karry Jean's ya esta registrado.\n\n🔑 Codigo: ${pedido.codigo_seguimiento}\n🔎 Sigue tu pedido aqui: ${url}`;
        const tel = (pedido.cliente_telefono || '').replace(/\D/g, '');
        window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
    };

    const contactWhatsApp = (pedido) => {
        const msg = `Hola ${pedido.cliente_nombre}! Nos comunicamos por tu pedido #${pedido.codigo_seguimiento || pedido.id?.slice(0, 8)} en Karry Jean's.`;
        const tel = (pedido.cliente_telefono || '').replace(/\D/g, '');
        window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
    };

    const getEstadoConfig = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];

    const filteredPedidos = filtroEstado === 'todos' ? pedidos : pedidos.filter(p => p.estado === filtroEstado);
    const nuevos = pedidos.filter(p => p.estado === 'nuevo').length;
    const totalVentas = pedidos.filter(p => p.estado !== 'cancelado').reduce((s, p) => s + (p.total || 0), 0);

    if (loading) return <div className="flex justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6" data-testid="pedidos-page">
            <div>
                <h1 className="text-2xl font-bold text-zinc-100">Pedidos de la Tienda</h1>
                <p className="text-zinc-400 mt-1">Pedidos recibidos desde la tienda publica. El cliente puede seguir su pedido con el codigo.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border border-zinc-700">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-amber-400/10 flex items-center justify-center">
                            <ClipboardList className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{pedidos.length}</p>
                            <p className="text-xs text-zinc-400">Total Pedidos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-zinc-700">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{nuevos}</p>
                            <p className="text-xs text-zinc-400">Nuevos</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-zinc-700">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-green-600">${totalVentas.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            <p className="text-xs text-zinc-400">Ventas Totales</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="flex gap-2 flex-wrap">
                <button onClick={() => setFiltroEstado('todos')}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filtroEstado === 'todos' ? 'bg-amber-400 text-zinc-900' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                    Todos ({pedidos.length})
                </button>
                {ESTADOS.map(e => (
                    <button key={e.value} onClick={() => setFiltroEstado(e.value)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filtroEstado === e.value ? 'bg-amber-400 text-zinc-900' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                        {e.label} ({pedidos.filter(p => p.estado === e.value).length})
                    </button>
                ))}
            </div>

            <Card className="border border-zinc-700">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800/50">
                                    <TableHead>Codigo</TableHead>
                                    <TableHead>Cliente</TableHead>
                                    <TableHead>Productos</TableHead>
                                    <TableHead>Envio</TableHead>
                                    <TableHead>Total</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredPedidos.length > 0 ? filteredPedidos.map((pedido) => {
                                    const estadoConfig = getEstadoConfig(pedido.estado);
                                    return (
                                        <TableRow key={pedido.id} data-testid={`pedido-row-${pedido.id}`}>
                                            <TableCell>
                                                <p className="font-mono text-sm font-bold text-amber-400">{pedido.codigo_seguimiento || '—'}</p>
                                                <p className="text-xs text-zinc-500 mt-0.5">{new Date(pedido.created_at).toLocaleDateString('es-MX')}</p>
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-medium text-zinc-100">{pedido.cliente_nombre}</p>
                                                <p className="text-xs text-zinc-400">{pedido.cliente_telefono}</p>
                                            </TableCell>
                                            <TableCell>
                                                {pedido.items?.slice(0, 3).map((item, i) => (
                                                    <p key={i} className="text-xs text-zinc-400">{item.nombre} (T:{item.talla}) x{item.cantidad}</p>
                                                ))}
                                                {pedido.items?.length > 3 && <p className="text-xs text-zinc-500 italic">+{pedido.items.length - 3} mas...</p>}
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-xs text-zinc-400 capitalize">{(pedido.metodo_envio || '—').replace('_', ' ')}</p>
                                                {pedido.ciudad && <p className="text-[10px] text-zinc-500">{pedido.ciudad}</p>}
                                                {pedido.numero_rastreo && (
                                                    <p className="text-[10px] text-amber-400 font-mono mt-0.5">📦 {pedido.numero_rastreo}</p>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-semibold text-zinc-100">${pedido.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                            <TableCell>
                                                <Select value={pedido.estado} onValueChange={(v) => updateEstado(pedido.id, v)}>
                                                    <SelectTrigger className={`w-32 h-8 text-xs font-medium rounded-full ${estadoConfig.color}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {ESTADOS.map(e => (<SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>))}
                                                    </SelectContent>
                                                </Select>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(pedido)}
                                                        className="text-blue-400 h-8 w-8 p-0" title="Editar pedido y rastreo"
                                                        data-testid={`btn-edit-pedido-${pedido.id}`}>
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => copyTrackingLink(pedido)}
                                                        className="text-zinc-400 h-8 w-8 p-0" title="Copiar link de seguimiento"
                                                        data-testid={`btn-copy-link-${pedido.id}`}>
                                                        <Copy className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => sendTrackingByWA(pedido)}
                                                        className="text-amber-400 h-8 w-8 p-0" title="Enviar link de seguimiento por WhatsApp"
                                                        data-testid={`btn-send-tracking-${pedido.id}`}>
                                                        <Hash className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => contactWhatsApp(pedido)}
                                                        className="text-green-500 h-8 w-8 p-0" title="Contactar por WhatsApp"
                                                        data-testid={`contact-pedido-${pedido.id}`}>
                                                        <MessageCircle className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }) : (
                                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-zinc-400">No hay pedidos</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal de edicion de pedido */}
            <Dialog open={!!editModal} onOpenChange={(o) => !o && setEditModal(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="modal-editar-pedido">
                    <DialogHeader>
                        <DialogTitle>Editar pedido <span className="font-mono text-amber-400 ml-2">{editModal?.codigo_seguimiento}</span></DialogTitle>
                    </DialogHeader>
                    {editModal && (
                        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                            <div className="bg-zinc-800 rounded-lg p-3 text-sm">
                                <p className="font-bold text-zinc-100">{editModal.cliente_nombre}</p>
                                <p className="text-xs text-zinc-400">{editModal.cliente_telefono}</p>
                                <p className="text-xs text-zinc-500 mt-1 capitalize">{editModal.metodo_envio}{editModal.ciudad ? ` • ${editModal.ciudad}` : ''}</p>
                                <p className="text-xs font-bold text-amber-400 mt-1">Total: ${editModal.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>

                            <div>
                                <Label className="text-zinc-400 text-xs uppercase">Estado del pedido</Label>
                                <Select value={editModal.estado} onValueChange={(v) => setEditModal({ ...editModal, estado: v })}>
                                    <SelectTrigger data-testid="edit-estado"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ESTADOS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-zinc-400 text-xs uppercase">Paqueteria</Label>
                                    <Select value={editModal.paqueteria || ''} onValueChange={onPaqueteriaChange}>
                                        <SelectTrigger data-testid="edit-paqueteria"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                        <SelectContent>
                                            {PAQUETERIAS_PRESET.map(p => <SelectItem key={p.v} value={p.v}>{p.v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-zinc-400 text-xs uppercase">Numero de rastreo / guia</Label>
                                    <Input
                                        value={editModal.numero_rastreo}
                                        onChange={(e) => onNumeroRastreoChange(e.target.value)}
                                        placeholder="Ej. 1234567890"
                                        className="font-mono"
                                        data-testid="edit-numero-rastreo"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="text-zinc-400 text-xs uppercase flex items-center justify-between">
                                    <span>Link de rastreo (WebView para el cliente)</span>
                                    {editModal.tracking_url && (
                                        <a href={editModal.tracking_url} target="_blank" rel="noreferrer" className="text-amber-400 hover:underline text-[10px] flex items-center gap-1">
                                            Probar <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                </Label>
                                <Input
                                    value={editModal.tracking_url}
                                    onChange={(e) => setEditModal({ ...editModal, tracking_url: e.target.value })}
                                    placeholder="https://www.dhl.com/..."
                                    data-testid="edit-tracking-url"
                                />
                                <p className="text-[10px] text-zinc-500 mt-1">Se autocompleta segun la paqueteria. El cliente vera esta pagina embebida en su seguimiento.</p>
                            </div>

                            <div>
                                <Label className="text-zinc-400 text-xs uppercase">Notas internas</Label>
                                <Textarea
                                    value={editModal.notas || ''}
                                    onChange={(e) => setEditModal({ ...editModal, notas: e.target.value })}
                                    rows={2}
                                />
                            </div>

                            <div className="bg-amber-400/10 border border-amber-400/30 rounded-lg p-3 text-xs">
                                <p className="font-bold text-amber-300 mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Link de seguimiento del cliente</p>
                                <p className="text-zinc-300 font-mono text-[11px] break-all">{window.location.origin}/pedido/{editModal.codigo_seguimiento}</p>
                                <div className="flex gap-2 mt-2">
                                    <Button size="sm" variant="outline" onClick={() => copyTrackingLink(editModal)} className="h-7 text-xs">
                                        <Copy className="w-3 h-3 mr-1" /> Copiar link
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => sendTrackingByWA(editModal)} className="h-7 text-xs bg-emerald-500/20 border-emerald-500/50 text-emerald-300">
                                        <MessageCircle className="w-3 h-3 mr-1" /> Mandar al cliente por WA
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditModal(null)} disabled={savingEdit}>Cancelar</Button>
                        <Button onClick={saveEdit} disabled={savingEdit} className="bg-amber-500 hover:bg-amber-600 text-zinc-900 font-bold" data-testid="btn-guardar-pedido">
                            {savingEdit ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
